import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ReactComponent as Gallery } from "../../../../assets/images/KnowledgeBase/gallery.svg";
import { ReactComponent as Video } from "../../../../assets/images/KnowledgeBase/video.svg";
import { ReactComponent as Code } from "../../../../assets/images/KnowledgeBase/code.svg";
import { toast } from 'react-toastify';
import { compressImage } from '../../../../utils/imageUtils';
import { FloatingToolbar } from './FloatingToolbar';
import { sanitizeHtml } from '../../../../utils/sanitizeHtml';


const ArticleEditor = ({ title, setTitle, content, setContent, isReadOnly = false }) => {
  const contentRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const savedSelectionRef = useRef(null);
  const isInitialized = useRef(false);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    link: false,
    unorderedList: false,
    fontSize: '3', // Default 16px
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false
  });

  useEffect(() => {
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }, []);

  useEffect(() => {
    if (contentRef.current && content) {
      const currentHtml = contentRef.current.innerHTML;
      if (!isInitialized.current || (currentHtml === '<p></p>' && content !== '<p></p>')) {
        contentRef.current.innerHTML = sanitizeHtml(content);
        isInitialized.current = true;

        // Check if we should show placeholder
        const textContent = contentRef.current.textContent || '';
        setShowPlaceholder(textContent.trim() === '');
      }
    }
  }, [content]);

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && contentRef.current) {
      const range = selection.getRangeAt(0);
      if (contentRef.current.contains(range.commonAncestorContainer)) {
        savedSelectionRef.current = range.cloneRange();
      }
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelectionRef.current && contentRef.current) {
      contentRef.current.focus();
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  }, []);


  const hasShownErrorRef = useRef(false);
  const minLengthTimeoutRef = useRef(null);

  const handleTitleChange = (e) => {
    const value = e.target.value;

    // Prevent input if more than 50 characters
    if (value.length > 50) {
      if (!hasShownErrorRef.current) {
        toast.error("Title cannot exceed 50 characters");
        hasShownErrorRef.current = true;
      }
      return;
    }

    // Reset the error flag when under limit
    if (value.length <= 50) {
      hasShownErrorRef.current = false;
    }

    setTitle(value);

    // Clear existing timeout
    if (minLengthTimeoutRef.current) {
      clearTimeout(minLengthTimeoutRef.current);
    }

    // Show error after user stops typing (1 second delay)
    minLengthTimeoutRef.current = setTimeout(() => {
      if (value.length > 0 && value.length < 3) {
        toast.error("Title must be at least 3 characters");
      }
    }, 1000);
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (minLengthTimeoutRef.current) {
        clearTimeout(minLengthTimeoutRef.current);
      }
    };
  }, []);

  const handleContentInput = () => {
    if (contentRef.current) {
      const htmlContent = contentRef.current.innerHTML;
      const textContent = contentRef.current.textContent || '';

      // Check if content is empty (only whitespace or empty tags)
      const isEmpty = textContent.trim() === '' ||
        htmlContent === '<p><br></p>' ||
        htmlContent === '<p></p>' ||
        htmlContent === '<br>' ||
        htmlContent === '';

      setShowPlaceholder(isEmpty);
      setContent(htmlContent);
    }
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z') {
        e.preventDefault();
        document.execCommand('undo', false, null);
        handleContentInput();
      } else if (e.key === 'y') {
        e.preventDefault();
        document.execCommand('redo', false, null);
        handleContentInput();
      }
    }
  };

  const handlePaste = useCallback((e) => {
    e.preventDefault();

    const pastedHtml = e.clipboardData.getData('text/html');
    const pastedText = e.clipboardData.getData('text/plain');

    const processPlainText = (text) => {
      if (!text) return;

      // Linkify URLs
      const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;

      // Escape HTML entities
      let cleanText = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

      cleanText = cleanText.replace(urlPattern, (url) => {
        return `<a href="${url}" style="color: #148DFD; text-decoration: underline;" target="_blank">${url}</a>`;
      });

      // Convert newlines to breaks
      const htmlFormatted = cleanText.replace(/\n/g, '<br>');
      document.execCommand('insertHTML', false, htmlFormatted);
    };

    if (pastedHtml) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(pastedHtml, 'text/html');

        // Recursive cleaner function
        const sanitizeNode = (node) => {
          // Remove comments which often contain metadata
          if (node.nodeType === 8) {
            node.remove();
            return;
          }

          if (node.nodeType === 1) { // Element
            // Remove data-* attributes (fixes Payload Too Large) and classes
            const attrsToRemove = [];
            for (const attr of node.attributes) {
              if (attr.name.startsWith('data-') || attr.name === 'class') {
                attrsToRemove.push(attr.name);
              }
            }
            attrsToRemove.forEach(name => node.removeAttribute(name));

            // Recursively clean children
            Array.from(node.childNodes).forEach(sanitizeNode);
          }
        };

        Array.from(doc.body.childNodes).forEach(sanitizeNode);

        // Insert the sanitized HTML
        document.execCommand('insertHTML', false, sanitizeHtml(doc.body.innerHTML));
      } catch (error) {
        console.error("Paste processing error:", error);
        if (pastedText) processPlainText(pastedText);
      }
    } else if (pastedText) {
      processPlainText(pastedText);
    }

    handleContentInput();
  }, [handleContentInput]);

  const getActiveFormats = useCallback(() => {
    const formats = {
      bold: false,
      italic: false,
      underline: false,
      link: false,
      unorderedList: false,
      fontSize: '3',
      justifyLeft: false,
      justifyCenter: false,
      justifyRight: false
    };

    try {
      // Check inline formatting using queryCommandState
      formats.bold = document.queryCommandState('bold');
      formats.italic = document.queryCommandState('italic');
      formats.underline = document.queryCommandState('underline');
      formats.justifyLeft = document.queryCommandState('justifyLeft');
      formats.justifyCenter = document.queryCommandState('justifyCenter');
      formats.justifyRight = document.queryCommandState('justifyRight');
      formats.unorderedList = document.queryCommandState('insertUnorderedList');

      // Get computed font size
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        // Ensure we gain the element node
        const element = node.nodeType === 3 ? node.parentElement : node;
        if (element) {
          const computedSize = window.getComputedStyle(element).fontSize;
          formats.fontSize = parseInt(computedSize) || 16;
        }
      }

      // Check link by traversing up the DOM
      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        while (node && node !== contentRef.current) {
          if (node.nodeName === 'A') {
            formats.link = true;
          }
          node = node.parentNode;
        }
      }
    } catch (e) {
      // Ignore errors from queryCommandState
    }

    return formats;
  }, []);

  const updateToolbarPosition = useCallback(() => {
    if (document.activeElement?.closest('.floating-toolbar-container')) {
      return;
    }

    const selection = window.getSelection();
    if (
      selection &&
      !selection.isCollapsed &&
      contentRef.current &&
      contentRef.current.contains(selection.anchorNode)
    ) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setToolbarPosition({
        top: rect.top + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX
      });
      setToolbarVisible(true);
      setActiveFormats(getActiveFormats());
    } else {
      setToolbarVisible(false);
    }
  }, [getActiveFormats]);

  useEffect(() => {
    document.addEventListener('selectionchange', updateToolbarPosition);
    return () => {
      document.removeEventListener('selectionchange', updateToolbarPosition);
    };
  }, []);

  const applyCustomFontSize = (size) => {
    // Use the temporary size=7 marker technique
    document.execCommand('fontSize', false, '7');
    if (contentRef.current) {
      const fontElements = contentRef.current.querySelectorAll('font[size="7"]');
      fontElements.forEach(font => {
        font.removeAttribute('size');
        font.style.fontSize = `${size}px`;
      });
    }
  };

  const execCommand = (command, value = null) => {
    // Restore selection if we lost focus (e.g. to the toolbar input)
    if (document.activeElement !== contentRef.current) {
      restoreSelection();
    }

    if (command === 'fontSize') {
      applyCustomFontSize(value);
    } else {
      document.execCommand(command, false, value);
    }

    if (contentRef.current) {
      setContent(contentRef.current.innerHTML);
    }
    setTimeout(() => {
      setActiveFormats(getActiveFormats());
    }, 0);
  };

  const insertAtCursor = useCallback((html) => {
    restoreSelection();

    if (contentRef.current) {
      contentRef.current.focus();

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        document.execCommand('insertHTML', false, html);
      } else {
        contentRef.current.innerHTML += html;
      }
      handleContentInput();
    }
  }, [restoreSelection]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        // Compress image to avoid large payloads
        const compressedDataUrl = await compressImage(file, { maxWidth: 800, quality: 0.6 });

        const imageHtml = `
          <div style="margin: 16px 0;">
            <img src="${compressedDataUrl}" alt="${file.name}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" />
          </div>
          <p></p>
        `;
        insertAtCursor(imageHtml);
      } catch (error) {
        console.error("Image compression error:", error);
        toast.error("Failed to process image");
      }
    }
    e.target.value = '';
  }, [insertAtCursor]);

  const handleVideoUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      // 2MB limit for embedded videos
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Video is too large (>2MB). Please use an external link instead.");
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const videoHtml = `
          <div style="margin: 16px 0;">
            <video controls style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <source src="${event.target.result}" type="${file.type}">
              Your browser does not support the video tag.
            </video>
          </div>
          <p></p>
        `;
        insertAtCursor(videoHtml);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  }, [insertAtCursor]);

  // Handle code block insertion - simple editable code block
  const handleCodeBlockInsert = useCallback(() => {
    const codeBlockHtml = `
      <div class="code-block-wrapper" style="margin: 16px 0;">
        <div 
          class="code-block-editable"
          contenteditable="true"
          style="
            background-color: #F9FAFB;
            border: 1px solid #D1D5DB;
            border-radius: 6px;
            padding: 12px 14px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
            font-size: 14px;
            line-height: 1.5;
            color: #374151;
            min-height: 40px;
            outline: none;
            white-space: pre-wrap;
            word-wrap: break-word;
          "
        ></div>
      </div>
      <p></p>
    `;

    insertAtCursor(codeBlockHtml);
  }, [insertAtCursor]);

  const handleGalleryClick = useCallback(() => {
    saveSelection();
    imageInputRef.current?.click();
  }, [saveSelection]);

  const handleVideoClick = useCallback(() => {
    saveSelection();
    videoInputRef.current?.click();
  }, [saveSelection]);

  const handleCodeClick = useCallback(() => {
    saveSelection();
    handleCodeBlockInsert();
  }, [saveSelection, handleCodeBlockInsert]);

  // Handle link insertion
  const handleInsertLink = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    const url = prompt('Enter the URL:', 'https://');
    if (url && url !== 'https://') {
      if (selectedText) {
        // Wrap selected text with link
        document.execCommand('createLink', false, url);
      } else {
        // Insert a new link with URL as text
        const linkHtml = `<a href="${url}" style="color: #3b82f6; text-decoration: underline;" target="_blank">${url}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
      }
      handleContentInput();
    }
  }, []);

  // Handle table insertion
  const handleInsertTable = useCallback(() => {
    saveSelection();
    const tableHtml = `
      <div style="margin: 16px 0; overflow-x: auto;">
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb;">
          <thead>
            <tr style="background-color: #f9fafb;">
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: left; font-weight: 600;">Header 1</th>
              <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: left; font-weight: 600;">Header 2</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 1</td>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 2</td>
            </tr>
            <tr>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 3</td>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 4</td>
            </tr>
            <tr>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 5</td>
              <td style="border: 1px solid #e5e7eb; padding: 12px;">Cell 6</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p></p>
    `;
    insertAtCursor(tableHtml);
  }, [saveSelection, insertAtCursor]);



  return (
    <div style={{ flex: 1, padding: '24px', width: '100%', paddingTop: '8px' }}>

      <div style={{ marginBottom: '16px' }}>
        <input
          value={title}
          onChange={handleTitleChange}
          placeholder="Article Title"
          disabled={isReadOnly}
          style={{
            width: '100%',
            fontFamily: 'Inter, sans-serif',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '18px',
            color: '#061522',
            border: 'none',
            outline: 'none',
            marginBottom: '16px',
            background: isReadOnly ? 'transparent' : 'inherit',
            cursor: isReadOnly ? 'not-allowed' : 'text',
          }}
          className="article-title-input"
        />


        <style>
          {`
    .article-title-input::placeholder {
      color: #93A1B0;
      font-family: Inter, sans-serif;
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: 18px;
      opacity: 1;
    }
    
    .code-block-editable:focus {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
    [contenteditable] ul, [contenteditable] ol {
      padding-left: 24px;
      margin: 8px 0;
    }
    [contenteditable] li {
      margin: 4px 0;
    }
    [contenteditable] a {
      color: #148DFD;
      text-decoration: underline;
    }
  `}

        </style>


        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          onChange={handleVideoUpload}
          style={{ display: 'none' }}
        />

        {/* Hide toolbar buttons for viewers (read-only mode) */}
        {!isReadOnly && (
          <div style={{ display: 'flex', gap: '16px', color: '#3b82f6' }}>
            <button
              onClick={handleGalleryClick}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '14px' }}
              title="Insert Image"
            >
              <Gallery />
            </button>
            <button
              onClick={handleVideoClick}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '14px' }}
              title="Insert Video"
            >
              <Video />
            </button>
            <button
              onClick={handleCodeClick}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '14px' }}
              title="Insert Code Block"
            >
              <Code />
            </button>
          </div>
        )}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0 30px 0' }} />

      <div style={{ position: 'relative' }}>
        {showPlaceholder && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '4px',
              color: '#93A1B0',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          >
            Start writing your article
          </div>
        )}
        <div
          ref={contentRef}
          className="content-editor"
          contentEditable={!isReadOnly}
          suppressContentEditableWarning
          onInput={isReadOnly ? undefined : handleContentInput}
          onBlur={isReadOnly ? undefined : saveSelection}
          onKeyDown={isReadOnly ? undefined : handleKeyDown}
          onPaste={isReadOnly ? undefined : handlePaste}
          style={{
            minHeight: '500px',
            outline: 'none',
            fontSize: '16px',
            lineHeight: '1.6',
            color: '#405261',
            direction: 'ltr',
            textAlign: 'left',
            unicodeBidi: 'plaintext',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            paddingLeft: '4px',
            cursor: isReadOnly ? 'not-allowed' : 'text',
            opacity: isReadOnly ? 0.8 : 1,
          }}
        />
      </div>

      {!isReadOnly && (
        <FloatingToolbar
          visible={toolbarVisible}
          position={toolbarPosition}
          onCommand={execCommand}
          activeFormats={activeFormats}
          onInsertLink={handleInsertLink}
        />
      )}
    </div>
  );
};

export default ArticleEditor;
